import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  type ElementRef,
  input,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faChevronDoubleDown } from '@fortawesome/pro-regular-svg-icons';
import { ButtonContainerComponent } from '@reconcept-platform/shared/ui/standalone/button/button-container.component';
import { fromEvent, map, switchMap } from 'rxjs';

@Component({
  selector: 'rcp-vertical-fixed-button-container',
  template: `
    <div
      #scrollContainer
      [class.max-height]="!!maxHeight()"
      [style.max-height]="maxHeight() === 0 ? 'auto' : maxHeight() + 'px'"
    >
      <ng-content></ng-content>
    </div>

    @defer (on idle) {
      @if (itemsBelowViewPort()) {
        <span class="more-items muted text">
          <fa-icon [icon]="faChevronDoubleDown"></fa-icon>
        </span>
      }
    }
  `,
  styles: `
    :host {
      position: relative;
    }

    .max-height {
      overflow-y: scroll;
    }

    span.more-items {
      position: absolute;
      right: 0;

      font-size: var(--font-size-small);
    }
  `,
  imports: [FaIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class VerticalScrollableButtonContainerComponent {
  maxHeight = input<number>(0);

  scrollContainer = viewChild<ElementRef>('scrollContainer');
  buttonContainer = contentChild(ButtonContainerComponent);
  resizeListener = toSignal(fromEvent(window, 'resize'));
  scrollListener = toSignal<number>(
    toObservable(this.scrollContainer).pipe(
      // @ts-expect-error TS18048 possibly undefined
      switchMap((buttonContainer) => fromEvent<Event>(buttonContainer.nativeElement, 'scroll')),
      map((event) => (event.target as HTMLElement).scrollTop),
    ),
  );

  itemsBelowViewPort = computed(() => {
    const currentScroll = this.scrollListener() ?? 0;

    // empty call to trigger this;
    this.resizeListener();

    // @ts-expect-error TS2532 object possibly undefined
    const buttonContainer = this.scrollContainer().nativeElement;
    const contentContainerHeight = buttonContainer.getBoundingClientRect().height;
    const scrollContainerHeight = buttonContainer.scrollHeight;
    const buttonHeight = 40; // 40 is the height of the button, as defined in scss
    const gap = 8;

    return Math.floor(scrollContainerHeight - contentContainerHeight - (buttonHeight - gap)) > currentScroll;
  });

  protected readonly faChevronDoubleDown = faChevronDoubleDown;
}
